'use client';

import moment, { Moment } from "moment"
import { useContext, useEffect, useMemo, useRef, useState } from "react"
import * as echarts from 'echarts'

import { categoricalColorSchemes } from "@nivo/colors"
import { useDarkMode } from "@/hooks/useDarkMode";
import { useIsMobile } from "@/hooks/isMobile";
import { Button } from "../ui/button";
import { TrimContext } from "@/contexts/trimContext";


export type ColorSchemeKey = keyof typeof categoricalColorSchemes;

export interface Node {
    name: string
}

export interface Link {
    source: string,
    target: string,
    value: number
}

export interface ChartData {
    nodes: Node[];
    links: Link[];
}

export interface TooltipConfig {
  seriesNameFormatter?: (name: string) => string; seriesValueFormatter?: (value: number) => string;
}

export interface SankeyChartProps {
  // Required
  data: ChartData;

  // Optional configuration
  title?: string;
  description?: string;
  height?: string;
  showLegend?: boolean;
  
  // Y-axis configuration
  yAxisFormatter?: (value: number) => string;
  yAxisMax?: number;
  yAxisScale?: boolean;
  connectNulls?: boolean;
  smooth?: boolean;
  
  // Tooltip configuration
  tooltip?: TooltipConfig;
  
  // Styling
  colorScheme?: ColorSchemeKey;
  stack?: boolean;
  stackOrder?: 'ascending' | 'descending' | 'insideOut' | 'none' | 'seriesDesc' | 'seriesAsc';
  symbol?: 'circle' | 'emptyCircle' | 'rect' | 'roundRect' | 'triangle' | 'diamond' | 'pin' | 'arrow' | 'none';

  showTotal?: boolean
}

const defaultProps: Partial<SankeyChartProps> = {
  height: 'h-full',
  showLegend: true,
  yAxisScale: true,
  colorScheme: 'tableau10',
  stackOrder: 'seriesDesc',
  symbol: 'none',
  connectNulls: false,
  smooth: false,
};

const defaultValueFormatter: (n: number) => string = (n: number) => {
  if (n > 1) {
    return n.toString()
  } else {
    return n.toExponential()
  }
}

export function SankeyChart({
  data,
  title,
  description,
  tooltip
}: SankeyChartProps) {
  const [chartInstance, setChartInstance] = useState<echarts.ECharts>()
  const chartRef = useRef<HTMLDivElement>(null)
  const isDark = useDarkMode()

  useEffect(() => {
    const element = chartRef.current;
    if (!element) return;

    // Ensure element has dimensions before initializing
    const initChart = () => {

      const _chartInstance = echarts.init(element);
      setChartInstance(_chartInstance);

      // Handle window resize
      const handleResize = () => {
        _chartInstance.resize();
      };

      // Handle container resize using ResizeObserver
      const resizeObserver = new ResizeObserver(() => {
        _chartInstance.resize();
      });
      resizeObserver.observe(element);

      return () => {
        window.removeEventListener('resize', handleResize);
        resizeObserver.disconnect();
        _chartInstance.dispose();
      };
    };

    // Start initialization
    const cleanup = initChart();

    return () => {
      cleanup?.();
    };
  }, [])

  useEffect(() => {
      const defaultTooltipFormatter = (
          params: any
      ) => {
          const formatter = tooltip?.seriesValueFormatter || ((v) => v)
          let resultGrid = `<div class='grid grid-cols-1 auto-cols-fr text-xs'>`
          if (params.data.name) {
              // we're hovering a dest.
              resultGrid += `<div class='col-span-1'><span>${params.data.name}: ${formatter(params.value)}</span></div>`
          } else {
              // hovering a connection
              //
              resultGrid += `<div class='col-span-1'><span>${params.data.source} -> ${params.data.target}: ${formatter(params.data.value)}</span></div>`
          }
          resultGrid += '</div>'

          return resultGrid
      }

    chartInstance?.setOption({
      darkMode: isDark,
      tooltip: {
          trigger: 'item',
          triggerOn: 'mousemove',
          formatter: defaultTooltipFormatter, //'{b0}: ${c0}'
      },
      series: {
          type: "sankey",
          data: data.nodes,
          links: data.links,
          levels: [
              {
                  depth: 0,
                  itemStyle: {
                      color: "#ccebc5"
                  },
                  lineStyle: {
                      color: "source",
                      opacity: 0.6,
                  }
              },
              {
                  depth: 1,
                  itemStyle: {
                      color: "#ccebc5"
                  },
                  lineStyle: {
                      color: "#ccebc5",
                      opacity: 0.6,
                  }
              },
              {
                  depth: 2,
                  itemStyle: {
                      color: "#b3cde3"
                  },
                  lineStyle: {
                      color: "#fbb4ae",
                      opacity: 0.6,
                  }
              },
              {
                  depth: 3,
                  itemStyle: {
                      color: "#fbb4ae"
                  },
                  lineStyle: {
                      color: "source",
                      opacity: 0.6,
                  }
              },
              {
                  depth: 4,
                  itemStyle: {
                      color: "#fbb4ae"
                  },
                  lineStyle: {
                      color: "source",
                      opacity: 0.6,
                  }
              }
          ]
      },
    })
  }, [isDark, chartInstance, data, title])

  const textColor = isDark?"#ccc":"#222"

  return (
    <div className='flex flex-col h-full lg:flex-row'>
      <div className='flex flex-col grow'> 
        { /* title */ }
        <div className="flex flex-row">
          <span className="block text-2xl lg:ml-8 ml-4 grow">{title}</span>
          <span className="block text-xs place-self-end">{description}</span>
        </div>
        { /* chart */}
        <div className='w-full h-full' ref={chartRef} />
      </div>
    </div>)
}
